import { ReactNode } from "react";
import clsx from "clsx";
import InfoTooltip from "@/components/UI/InfoTooltip";
import featureFlagStore from "@/store/featureFlagStore";
import useTableUIStore from "@/store/tableUIStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import requestInformationStore from "@/store/requestInformationStore";
import RequestInformationStrip from "@/components/widgets/tradeActionSummary/tradeSettings/RequestInformationStrip";
import { getExecutionRequestIdForCP } from "@/components/widgets/tradeActionSummary/tradeSettings/RequestInformationStrip/requestInformationStripHelpers";
import { getFontSizeClass } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { SettingDropdown } from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/SettingDropdown";
import { SettingNumericInput } from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/SettingNumericInput";
import { resolveSettingForCP } from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/SelectPanelHelper";
import {
  isSameOverrideFieldValue,
  isTradeLevelFieldPending,
  isTradeWideFieldPending,
} from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/CPSettingsGridHelper";
import {
  ICEBERG_AT_MAX_TOOLTIP,
  getEffectiveIcebergFloorForCP,
} from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/CPSettingsGridIcebergHelpers";
import {
  isSideAndDirectionEditable,
  isTradeWideSettingEditable,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeWideSettingGating";
import { buildCounterpartiesListFromTrade } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/counterpartiesFromTrade";
import { isCoverSettingLockedBySingleCP } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/coverSettingsRule";
import {
  isSideLockedToOneWay,
  sideLockReason,
  tradeContainsCloseLeg,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/unwindSideRule";
import { useNPVSideLock } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/useNPVSideLock";
import {
  CondensedSettingField,
  anyCpWithholdsDirection,
  buildCondensedColumns,
  buildCondensedGridTemplate,
  condensedGridMinWidthPx,
  condensedYesNoWidthPx,
  isCpOneWay,
  resolveCpColumnLock,
} from "./condensedGridColumnsHelper";
import {
  IToggleButton,
  IToggleButtonToReturnBoolean,
  IToggleCounterpartiesButton,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeSettingsInterfaces";

// LQC-1301 (experiment) — condensed trade-settings layout for the left-rail
// Trade Settings slide-in. Deliberately a trimmed view of CPSettingsGrid:
//   • Auto Affirm shown as a trade-wide ("OP") row at the top.
//   • Per-CP grid shows Side / Direction / Iceberg / Advise Comp / Advise Cover
//     / Cover Spread — no All/Custom toggle, no Set Level.
// It reuses the SAME controls (SettingDropdown / SettingNumericInput) and store
// wiring as the full grid, so edits write through identical actions.
//
// LQC-2271 — Side + Direction were added so the OP can release the direction
// without waiting for the RP to request it. Both drop out of the grid once the
// trade reaches Awarded (the side is released by then) and for Orders (always
// One-Way), which is also what keeps the row narrow enough to read in the
// common late-stage case. The row scrolls horizontally when it doesn't fit.

const TradeSettingsCondensedGrid = ({
  esid,
  data,
}: {
  esid: string;
  data: ExecutionStrategy;
}) => {
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  const dropdownWidthPx = condensedYesNoWidthPx(fontSizeSetting);

  const settings = tradeSettingsStore(
    (state) => state.tradeSettingsData.find((t) => t.esid === esid)?.settings,
  );
  const editBaseline = tradeSettingsStore(
    (state) => state.editBaselineOverrides[esid],
  );
  const tradeWideBaseline = tradeSettingsStore(
    (state) => state.editTradeWideBaselineByEsid[esid],
  );
  const tradeLevelBaseline = tradeSettingsStore(
    (state) => state.editTradeLevelBaselineByEsid[esid],
  );
  const knownCpCodes = tradeSettingsStore(
    (state) => state.editKnownCpCodes[esid],
  );
  // LQC-1938 - backend-driven flag re-enabling Iceberg (releaseSize) for Orders.
  const icebergForOrders = featureFlagStore((state) => state.icebergForOrders);
  // LQC-637 — per-ER request-information statuses for this ES. New object
  // reference only when this esid's map is upserted, so unrelated trades don't
  // re-render. Drives the conditional per-CP RequestInformationStrip below.
  const requestInfoForEsid = requestInformationStore(
    (state) => state.requestInfo[esid],
  );
  // LQC-2271 — shared with the Edit Panel grid: an NPV block forces One-Way +
  // Direction SHOW and locks Side. Called before the early return below so the
  // hook order stays stable.
  const npvInTrade = useNPVSideLock(esid);

  if (!settings) return null;

  const status = data?.status;
  // Derive trade type from the authoritative trade data (data.type), NOT from
  // settings.tradeTypes. The settings store is hydrated from the Edit/Settings
  // API's `requestType` and defaults to RFQ when absent — so on a page refresh
  // (empty settings store) an Order whose response lacks/omits requestType would
  // be misread as RFQ, wrongly surfacing Cover Spread + the per-CP grid. The
  // parent TradeSettingsSidebarContent already gates on selectedTrade.type; this
  // keeps the two in agreement. (CLAUDE.md: trade `type` is the determinant.)
  const isOrder = data?.type === "Order";
  // Iceberg is locked for Orders unless the IcebergForOrders feature is on.
  // The grid renders for Orders (flag ON) only so Iceberg can be adjusted
  // mid-life; the dropdown columns stay Order-disabled (LQC-1938).
  const icebergDisabledForOrder = isOrder && !icebergForOrders;
  // LQC-1707: the slide-in shows ONLY the CPs already on the execution strategy,
  // derived directly from the trade — never the shared counterpartiesList slice,
  // which the Edit Panel owns and fills with the full universe of available CPs
  // (incl. CPs the user is provisionally adding/removing).
  const activeCounterparties = buildCounterpartiesListFromTrade(data);
  const activeCpCodes = activeCounterparties.map(
    (cp: IToggleCounterpartiesButton) => cp.code,
  );

  // Trade-wide (OP-level) editability mirrors the blotter gear popover.
  const autoAffirmEditable = isTradeWideSettingEditable(
    "autoAffirm",
    status,
    isOrder,
  );
  // LQC-1705: Cover Spread is per-CP, but retains its status ceiling (RFQ only,
  // editable through to Affirmed). Used to disable the per-CP Cover Spread cell.
  const coverSpreadEditable =
    !isOrder &&
    isTradeWideSettingEditable("releaseCoverSpread", status, isOrder);

  // LQC-1704: per-CP Advise Cover / Cover Spread only apply when sending to more
  // than one CP — lock both when a single CP is active. Scoped to RFQs (matches
  // the Edit Panel): Orders can never have >1 CP, so the ">1 counterparty"
  // tooltip would mislead - Orders are locked via lockedByOrder instead.
  const lockCoverBySingleCp =
    !isOrder && isCoverSettingLockedBySingleCP(activeCounterparties.length);

  // LQC-2271 — Side / Direction. Hidden for Orders (permanently One-Way, so the
  // columns would only ever be dead weight in a panel this narrow) and from
  // Awarded onwards. Direction earns its column only while some CP is actually
  // withholding it.
  const sideLockInputs = {
    containsClose: tradeContainsCloseLeg(data),
    containsNPV: npvInTrade,
    isOrder,
  };
  const showSide = !isOrder && isSideAndDirectionEditable(status);
  const showDirection =
    showSide && anyCpWithholdsDirection(settings, activeCpCodes);
  const columns = buildCondensedColumns({
    showSide,
    showDirection,
    fontSize: fontSizeSetting,
  });
  const gridTemplateColumns = buildCondensedGridTemplate(columns);
  const gridMinWidth = `${condensedGridMinWidthPx(columns)}px`;
  const rowGridStyle = { gridTemplateColumns, minWidth: gridMinWidth };

  const autoAffirmPending =
    isTradeWideFieldPending(
      settings.autoAffirm as IToggleButton[] | IToggleButtonToReturnBoolean[],
      tradeWideBaseline?.autoAffirm,
    ) || undefined;

  return (
    <div
      data-testid="TradeSettingsCondensedGridContainer"
      id="TradeSettingsCondensedGridContainer"
      className={clsx("flex w-full min-w-0 flex-col gap-3", fontSizeClass)}
    >
      {/* ===== Trade-wide (OP) settings ===== */}
      <div className="flex flex-col gap-2">
        <TradeWideRow
          heading="Auto Affirm"
          infoMessage={autoAffirmInfo(isOrder)}
          dropdownWidthPx={dropdownWidthPx}
        >
          <SettingDropdown
            settingArray={
              settings.autoAffirm as
                | IToggleButton[]
                | IToggleButtonToReturnBoolean[]
            }
            esid={esid}
            settingField="autoAffirm"
            fontSizeClass={fontSizeClass}
            disabled={!autoAffirmEditable}
            isPending={autoAffirmPending}
          />
        </TradeWideRow>
      </div>

      {/* ===== Per-CP grid ===== */}
      {!icebergDisabledForOrder && activeCounterparties.length > 0 && (
        <div
          data-testid="TradeSettingsCPGrid"
          className="min-w-0 border-t border-gray-200 pt-2 dark:border-gray-700"
        >
          {/* LQC-2271: the panel is as narrow as 220px, so once Side +
              Direction are in the row it can no longer fit. Every row lives in
              this one scroller so the header and CP rows stay aligned. The
              scrollbar is styled always-visible - the off-screen columns are
              undiscoverable without it. */}
          <div className="condensed-grid-scroll">
            {/* Header row */}
            <div className="mb-1 grid items-end gap-2" style={rowGridStyle}>
              <div className="uppercase tracking-wider text-gray-500 dark:text-gray-400">
                CP
              </div>
              {columns.map((col) => (
                <div
                  key={col.settingField}
                  className="uppercase leading-tight tracking-wider text-gray-500 dark:text-gray-400"
                >
                  {col.heading}
                </div>
              ))}
            </div>

            {/* One row per active CP */}
            {activeCounterparties.map((cp: IToggleCounterpartiesButton) => {
              const cpOverride = settings.cpSettingsOverrides[cp.code] || {};
              const cpBaseline = editBaseline?.[cp.code];

              const overrideState = (field: CondensedSettingField) => {
                const isOverridden = cpOverride[field] !== undefined;
                const isPending = editBaseline
                  ? !isSameOverrideFieldValue(
                      cpOverride[field],
                      cpBaseline?.[field],
                    )
                  : isOverridden;
                // No override → follows the trade-level default; muted orange when
                // that default is itself pending.
                const isFollowingPending =
                  !isOverridden &&
                  isTradeLevelFieldPending(settings, tradeLevelBaseline, field);
                return { isOverridden, isPending, isFollowingPending };
              };

              // Iceberg floor (per-CP) — reuse the full grid's enforcement.
              const icebergValue = resolveSettingForCP(
                settings,
                cp.code,
                "releaseSize",
              ) as number | undefined;
              const isApiKnownCp = (knownCpCodes ?? []).includes(cp.code);
              const icebergFloor = getEffectiveIcebergFloorForCP(
                editBaseline,
                cp.code,
                isApiKnownCp,
              );
              const icebergGreyedOut =
                Number(icebergValue) === 100 && icebergFloor === 100;
              const showFloorTooltip =
                !icebergGreyedOut &&
                icebergFloor !== undefined &&
                icebergFloor < 100;

              // LQC-637 — this CP's executionRequestId + its request statuses, used
              // by the conditional RequestInformationStrip rendered beneath the row.
              const erId = getExecutionRequestIdForCP(data, cp.code);
              const cpRequestStatuses =
                erId != null ? requestInfoForEsid?.[String(erId)] : undefined;

              return (
                // Wrap each CP's row + its request strip so a divider line and
                // spacing sit between CPs (mirrors the RP view's per-row treatment).
                <div
                  key={cp.code}
                  className="mb-2 border-b border-gray-200 pb-2 dark:border-gray-700"
                  // Floor width matches the row so the divider spans the whole
                  // scrolled width, not just the visible panel.
                  style={{ minWidth: gridMinWidth }}
                >
                  <div
                    data-testid={`TradeSettingsCPRow-${cp.code}`}
                    id={`TradeSettingsCPRow-${cp.code}`}
                    className="mb-1 grid items-center gap-2"
                    style={rowGridStyle}
                  >
                    <div className="font-medium">{cp.code}</div>
                    {columns.map((col) => {
                      const { isOverridden, isPending, isFollowingPending } =
                        overrideState(col.settingField);

                      if (col.settingField === "releaseSize") {
                        return (
                          <SettingNumericInput
                            key={col.settingField}
                            value={icebergValue}
                            esid={esid}
                            settingField="releaseSize"
                            cpCode={cp.code}
                            fontSizeClass={fontSizeClass}
                            min={1}
                            max={100}
                            suffix="%"
                            isOverridden={isOverridden}
                            isPending={isPending}
                            isFollowingPending={isFollowingPending}
                            disabled={icebergGreyedOut}
                            tooltipId={
                              icebergGreyedOut
                                ? `condensedIcebergLocked-${esid}-${cp.code}`
                                : showFloorTooltip
                                  ? `condensedIcebergFloor-${esid}-${cp.code}`
                                  : undefined
                            }
                            tooltipMessage={
                              icebergGreyedOut
                                ? ICEBERG_AT_MAX_TOOLTIP
                                : showFloorTooltip
                                  ? `Must be ${icebergFloor}% or above`
                                  : undefined
                            }
                          />
                        );
                      }

                      // A One-Way CP always shows its direction, so there is
                      // nothing to release — reserve the cell rather than
                      // rendering a control that can't mean anything.
                      if (
                        col.settingField === "releaseDirection" &&
                        isCpOneWay(settings, cp.code)
                      ) {
                        return (
                          <div
                            key={col.settingField}
                            data-testid={`TradeSettingsDirectionNA-${cp.code}`}
                            className="text-center text-gray-400 dark:text-gray-500"
                          >
                            -
                          </div>
                        );
                      }

                      const lock = resolveCpColumnLock({
                        settingField: col.settingField,
                        isOrder,
                        lockCoverBySingleCp,
                        coverSpreadEditable,
                        sideLocked: isSideLockedToOneWay(sideLockInputs),
                        sideLockMessage: sideLockReason(sideLockInputs),
                      });
                      return (
                        <SettingDropdown
                          key={col.settingField}
                          settingArray={
                            resolveSettingForCP(
                              settings,
                              cp.code,
                              col.settingField,
                            ) as
                              | IToggleButton[]
                              | IToggleButtonToReturnBoolean[]
                          }
                          esid={esid}
                          settingField={col.settingField}
                          cpCode={cp.code}
                          fontSizeClass={fontSizeClass}
                          isOverridden={isOverridden}
                          isPending={isPending}
                          isFollowingPending={isFollowingPending}
                          disabled={lock.disabled}
                          tooltipId={
                            lock.tooltipKey
                              ? `condensed-${lock.tooltipKey}-${esid}-${cp.code}`
                              : undefined
                          }
                          tooltipMessage={lock.tooltipMessage}
                        />
                      );
                    })}
                  </div>
                  {erId != null && (
                    <RequestInformationStrip
                      esid={esid}
                      executionRequestId={erId}
                      statuses={cpRequestStatuses}
                      cpCode={cp.code}
                      fontSizeClass={fontSizeClass}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Small presentational helper for the trade-wide rows ---
const TradeWideRow = ({
  heading,
  infoMessage,
  dropdownWidthPx,
  children,
}: {
  heading: string;
  infoMessage?: string;
  suffix?: string;
  dropdownWidthPx: number;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="flex items-center whitespace-nowrap text-gray-600 dark:text-gray-300">
      {heading}
      {infoMessage && (
        <InfoTooltip id={`condensed-${heading}-info`} message={infoMessage} />
      )}
    </span>
    <div className="flex items-center gap-1">
      <div style={{ width: dropdownWidthPx }}>{children}</div>
    </div>
  </div>
);

const autoAffirmInfo = (isOrder: boolean) =>
  isOrder
    ? "Automatically affirm trades 60 seconds after Filled (including No More Fills)"
    : "Automatically affirm trades 60 seconds after Executed";

export default TradeSettingsCondensedGrid;
